import { Link, useLocation } from "wouter";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Menu, X, Gamepad2, Layers, BarChart3, Hammer, User, TrendingUp, Bug, FlaskConical } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";

export default function Layout({ children }) {
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [isPasswordlessMode, setIsPasswordlessMode] = useState(false);
  const { user, login, logout, loading, passwordlessSignIn } = useAuth();

  const navItems = [
    { href: "/", label: "Home", icon: null },
    { href: "/play", label: "Play", icon: Gamepad2 },
    { href: "/cards", label: "Cards", icon: Layers },
    { href: "/decks", label: "Decks", icon: Hammer },
    { href: "/meta", label: "Meta", icon: TrendingUp },
    { href: "/stats", label: "Stats", icon: BarChart3 },
    { href: "/card-debug", label: "Debug", icon: Bug },
    { href: "/card-sandbox", label: "Sandbox", icon: FlaskConical },
  ];

  const handleLogin = async (e) => {
    e.preventDefault();

    if (isPasswordlessMode) {
      // Passwordless sign-in
      if (identifier) {
        const result = await passwordlessSignIn(identifier);
        if (result.success) {
          setIsLoginModalOpen(false);
          // Reset form
          setIdentifier('');
        } else {
          alert(result.message || 'Sign in failed. Please check your identifier.');
        }
      }
    } else {
      // Regular login
      if (username && password) {
        const result = await login(username, password);
        if (result.success) {
          setIsLoginModalOpen(false);
          // Reset form
          setUsername('');
          setPassword('');
        } else {
          alert(result.message || 'Login failed. Please check your credentials.');
        }
      }
    }
  };

  const handleLogout = () => {
    logout();
  };

  // Listen for the custom event to open the login modal
  useEffect(() => {
    const handleOpenLoginModal = () => {
      setIsLoginModalOpen(true);
    };

    window.addEventListener('openLoginModal', handleOpenLoginModal);

    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModal);
    };
  }, []);

  return (
    <div className="h-screen flex flex-col bg-background text-foreground font-sans selection:bg-primary selection:text-primary-foreground">
      {/* Navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
        <div className="w-full flex h-16 items-center justify-between px-2 sm:px-4">
          <div className="flex items-center gap-8">
            <Link to="/">
              <div className="flex items-center gap-2 font-bold text-xl tracking-tighter hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center text-primary-foreground">
                  <Gamepad2 className="w-5 h-5" />
                </div>
                <span className="font-display text-2xl hidden sm:inline-block">CROMOBOUND</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <Link key={item.href} to={item.href}>
                  <div
                    className={cn(
                      "text-sm font-medium transition-colors hover:text-primary flex items-center gap-2",
                      location === item.href
                        ? "text-primary"
                        : "text-muted-foreground"
                    )}
                  >
                    {item.icon && <item.icon className="w-4 h-4" />}
                    {item.label}
                  </div>
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-3">
                <div className="hidden sm:flex items-center gap-2 text-sm font-medium">
                  <User className="w-4 h-4" />
                  <span>{user.username}</span>
                </div>
                <Button variant="outline" size="sm" className="border-primary/20 hover:border-primary/50 hover:bg-primary/10 text-primary" onClick={handleLogout}>
                  Logout
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:flex border-primary/20 hover:border-primary/50 hover:bg-primary/10 text-primary"
                onClick={() => setIsLoginModalOpen(true)}
              >
                Sign In
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border/40 bg-background p-4 space-y-4 animate-in slide-in-from-top-5">
            <nav className="flex flex-col gap-4">
              {navItems.map((item) => (
                <Link key={item.href} to={item.href}>
                  <div
                    className={cn(
                      "text-base font-medium transition-colors hover:text-primary flex items-center gap-3 p-2 rounded-md hover:bg-muted/50",
                      location === item.href
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground"
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.icon && <item.icon className="w-5 h-5" />}
                    {item.label}
                  </div>
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border/40">
                {user ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 p-2">
                      <User className="w-4 h-4" />
                      <span>{user.username}</span>
                    </div>
                    <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>Logout</Button>
                  </div>
                ) : (
                  <Button variant="outline" className="w-full justify-start" onClick={() => {setIsLoginModalOpen(true); setIsMobileMenuOpen(false);}}>Sign In</Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className={`flex-1 relative px-2 sm:px-4 ${location === '/decks' ? 'min-h-0 overflow-hidden' : ''}`}>
        {children}
      </main>

      {/* Login Modal */}
      {isLoginModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div
            className="bg-card border border-border/50 rounded-xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Sign In</h3>
                <button
                  className="text-2xl text-muted-foreground hover:text-foreground"
                  onClick={() => setIsLoginModalOpen(false)}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleLogin}>
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      className={`flex-1 py-2 px-4 rounded-md text-center ${
                        !isPasswordlessMode
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border border-border text-foreground'
                      }`}
                      onClick={() => setIsPasswordlessMode(false)}
                    >
                      Regular Login
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 px-4 rounded-md text-center ${
                        isPasswordlessMode
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border border-border text-foreground'
                      }`}
                      onClick={() => setIsPasswordlessMode(true)}
                    >
                      Passwordless
                    </button>
                  </div>

                  {isPasswordlessMode ? (
                    <div>
                      <label htmlFor="identifier" className="block text-sm font-medium mb-1">Username or Email</label>
                      <input
                        id="identifier"
                        type="text"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                        placeholder="Enter your username or email"
                        required
                      />
                    </div>
                  ) : (
                    <>
                      <div>
                        <label htmlFor="username" className="block text-sm font-medium mb-1">Username</label>
                        <input
                          id="username"
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                          placeholder="Enter your username"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="password" className="block text-sm font-medium mb-1">Password</label>
                        <input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground"
                          placeholder="Enter your password"
                          required
                        />
                      </div>
                    </>
                  )}

                  <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                    Sign In
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Footer - Hidden during gameplay and deck builder */}
      {location !== '/play' && location !== '/decks' && (
        <footer className="border-t border-border/40 bg-muted/20 py-12 mt-12">
          <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-8 px-2 sm:px-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 font-bold text-xl">
                <div className="w-6 h-6 bg-primary rounded-sm flex items-center justify-center text-primary-foreground">
                  <Gamepad2 className="w-4 h-4" />
                </div>
                <span className="font-display">CROMOBOUND</span>
              </div>
              <p className="text-sm text-muted-foreground">
                The ultimate companion for your card gaming journey. Build decks, track stats, and compete with friends.
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-foreground">Resources</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">Card Database</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Deck Builder</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Meta Stats</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Tournaments</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-foreground">Community</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">Discord</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Twitter</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Reddit</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Support</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4 text-foreground">Legal</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="w-full mt-12 pt-8 border-t border-border/40 text-center text-sm text-muted-foreground px-2 sm:px-4">
              <p>&copy; {new Date().getFullYear()} Cromobound. All rights reserved. Not affiliated with Riot Games.</p>
          </div>
        </footer>
      )}
    </div>
  );
}
